<template>
    <div>
        <div v-if="!loaded" class="w-full flex justify-center p-2">
            <c-loader color="rgba(var(--vs-primary), 1)"></c-loader>
        </div>

        <ds-table v-if="loaded" :max-items="this.integrations.length" :data="this.integrations" :heads="heads" :paginate="false" spacer>
            <template v-slot:geo="{geo}">
                <p class="product-name font-medium truncate">{{ geo.geo_name }}</p>
            </template>

            <template v-slot:model="{model}">
                <p class="product-name font-medium truncate">{{ model.traffic_model_name }}</p>
            </template>

            <template v-slot:country="{country}">
                <vs-chip v-tooltip.position.left="country.countries.join('<br>')" class="inline-block float-none">
                    <vs-avatar color="#f0f0f0" text-color="#175ea5" v-bind="$iconData('icon-globe', 'fi-rr-globe')"/>
                    <span>{{ country.countriesLength }}</span>
                </vs-chip>
            </template>

            <template v-slot:offers="{offers}">
                <vs-chip class="inline-block float-none" v-tooltip.position.left="offers.offers_accepted.join('<br>')">
                    <vs-avatar color="#f0f0f0" text-color="#175ea5" v-bind="$iconData('icon-briefcase', 'fi-rr-briefcase')"/>
                    <span style="color: #28C76F">{{ offers.offersLength }}</span> <span style="padding: 0 5px"> | </span> <span style="color: #EA5758">{{ offers.offers_not_accepted.length }}</span>
                </vs-chip>
            </template>

            <template v-slot:synchronize="{synchronize}">
                <vs-chip class="inline-block float-none" :color="synchronize.is_sync_available ? 'success' : 'danger'" transparent>
                    <vs-avatar :text-color="synchronize.is_sync_available ? 'success' : 'danger'" :color="synchronize.is_sync_available ? 'success' : 'danger'" v-bind="$iconData(synchronize.is_sync_available ? 'icon-check' : 'icon-x', synchronize.is_sync_available ? 'fi-rr-check' : 'fi-rr-cross-small')" />
                    <span>{{i('statistic.Sync')}}</span>
                </vs-chip>
            </template>

            <template v-slot:status="{status}">
                <div>
                    <vs-switch :disabled="loading" v-can="'settings_advertiser.change_status'" @click.stop.prevent="changeAdvertiserStatus(status)" v-model="status.is_active" color="success" icon-pack="feather" vs-icon-off="icon-x" vs-icon-on="icon-check"  class="m-auto"/>
                    <vs-switch v-can:show="'settings_advertiser.change_status'" disabled v-model="status.is_active" color="success" icon-pack="feather" vs-icon-off="icon-x" vs-icon-on="icon-check" class="m-auto"/>
                </div>
            </template>

            <template v-slot:action="{action}">
                <div class="flex items-center h-full">
                    <vs-button :disabled="loading" v-can="'settings_advertiser.update'" radius color="primary" type="flat" v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')" @click="editIntegration(action)" v-tooltip="i('modals.Edit_integration')"></vs-button>
                    <vs-button v-can:show="'settings_advertiser.update'" radius color="primary" type="flat" v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')" disabled></vs-button>
                    <vs-button :disabled="loading" radius color="primary" type="flat" v-bind="$iconData('icon-git-pull-request', 'fi-rr-code-merge')" disabled ></vs-button>
                    <vs-button :disabled="loading" v-can="'settings_advertiser.replicate'" v-if="servers" @click.stop="sync(action)" v-tooltip="$t('buttons.sync')" radius color="primary" type="flat" v-bind="$iconData('icon-shuffle', 'fi-rr-shuffle')"></vs-button>
                    <vs-button :disabled="loading" v-can="'settings_advertiser.check'" size="small" class="mr-1 ml-1 testButton" color="primary" type="border" @click="test(action)" >{{i('buttons.Test')}}</vs-button>
                    <vs-button v-can:show="'settings_advertiser.check'" disabled size="small" class="mr-1 ml-1 testButton" color="primary" type="border">{{i('buttons.Test')}}</vs-button>
                </div>
            </template>
        </ds-table>
    </div>
</template>

<script>
import i18n from '@/i18n/i18n'
import Table from '@/components/custom-table/Table'
import ErrorHandler from '@/utils/errorHandler'
import send from '@/utils/fetch'
import EventBus from '@/store/eventBus'
import {notifications} from '@/utils/mixins'

export default {
    props: ['detailsData', 'loading', 'available_fields'],

    mixins: [notifications],

    data() {
        return {
            integrations: [],
            dayOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            heads: [
                { name: 'global.Name', key: 'name', sortKey: 'name', value: 'name', display: true},
                { name: 'global.Geo', key: 'geo', sortKey: 'geo_name', value: 'geo', headerPosition: 'center', display: true},
                { name: 'global.Model', key: 'model', sortKey: 'traffic_model_name', value: 'model', headerPosition: 'center', display: true},
                { name: 'global.Country', key: 'country', sortKey: 'countriesLength', value: 'country', headerPosition: 'center', display: true},
                { name: 'global.Offers', key: 'offers', sortKey: 'offersLength', value: 'offers', headerPosition: 'center', display: true},
                { name: 'global.Synchronize', key: 'synchronize', sortKey: 'is_sync_available', value: 'synchronize', headerPosition: 'center', width: '150px', display: true},
                { name: 'global.Status', key: 'status', sortKey: 'is_active', value: 'status', headerPosition: 'center', width: '60px', display: true},
                { name: 'global.Actions', key: 'action', value: 'action', headerPosition: 'end', width: '230px', display: true},
            ],

            advId: null,
            advIntId: null,
            loaded: false,
        }
    },

    components: {
        Table
    },

    computed: {
        servers() {
            return (JSON.parse(process.env.VUE_APP_API_SERVERS) || []).length
        },
    },

    methods: {
        i (val) {
            return i18n.tc(val)
        },

        editIntegration(data) {
            const params = {
                ...data,
                available_fields: this.available_fields,
                adv: this.detailsData
            }

            EventBus.$emit('emitPopupEvent', {type: 'editIntegration', name: this.i('modals.Edit_integration'), ids: params})
        },

        changeAdvertiserStatus(data) {
            this.advIntId = data.id

            this.$vs.dialog({
                type: 'confirm',
                color: 'warning',
                title: this.i('confirm.Confirm_action'),
                text: data.is_active ? this.i('confirm.deactivateIntegration') : this.i('confirm.activateIntegration'),
                accept: this.confirmStatus,
                acceptText: (data.is_active ? this.i('buttons.Deactivate'): this.i('buttons.Activate')).toUpperCase(),
                cancelText: this.i('global.Cancel'),
            })
        },

        async confirmStatus() {
            try {
                await send(this.$url.advrtsr_integration.destroy, 'DELETE', {data: {id: this.advIntId}})
                await this.get()
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        async sync(data) {
            const response = await send(`${this.$url.advrtsr_integration.replicate}/${data.id}/replicate`, 'POST', {}, false)
                .then(response => response.data.result.responses)

            this.syncServer(response, 3000)
        },

        async test(data) {
            EventBus.$emit('emitPopupEvent', {type: 'testIntegration', name: this.i('buttons.Test'), ids: {...data, adv: this.detailsData}})
        },

        async get() {
            await send(`${this.$url.advrtsr.integrations}/${this.detailsData.id}/integrations`, 'GET')
                .then(response => response.data.result.integrations)
                .then(data => {
                    data.map(item => {
                        item.countriesLength = item.countries.length
                        item.offersLength = item.offers_accepted.length

                        return item
                    })
                    this.integrations = data
                })
        }
    },

    async mounted() {
        EventBus.$on(`updateAdvertiserDetails-${  this.detailsData.id}`, async () => {
            await this.get()
        })

        await this.get()
        this.loaded = true
    },
}
</script>

<style lang="scss">
.textchip-col.days-chip {
    min-width: 70px;
    padding-right: 0;
}

.textchip-col .text-chip {
    flex-direction: column!important;
    margin-left: 0!important;
}

.testButton {
    .con-vs-loading {
        position: absolute !important;
    }
}

.days-btn {
    /*border-color: #dcdcdc;*/
    width: 60px;
    margin: 5px auto;
    background: #C3C3C3;
    color: #fff;
    /*font-size: 0.5rem;*/
    font-size: 0.55rem !important;
    padding: 0.4rem 0.3rem !important;
    text-align: center;
    &.active {
        background: #28C76F;
        color: #fff;
    }
    &.active2 {
        background: #DFF7EA;
        color: #28C76F;
    }
}
.days-time {
    font-size: 0.6rem;
    margin-bottom: 5px;
}
.con-vs-chip {
    margin-bottom: 0;
}
</style>
